import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import React, {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DataTable,
  DataTableEmpty,
  DataTableGroupByDropdown,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  useDataTableState,
  UNASSIGNED_GROUP_KEY,
  Box,
  Button,
  MenuItem,
  Separator,
  Text,
  Icon,
  ToolbarAction,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type BulkAction,
  type Omittable,
} from "@salestrak/ui";
import { ViewSearch } from "#app/components/filters/view-search";
import { FilterPanel } from "#app/components/filters/filter-panel";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { AssigneeFilterChip } from "#app/components/filters/assignee-filter-chip";
import { ASSIGNEE_PRINCIPAL_FIELD } from "#app/lib/filters/build-where";

import {
  IssueListDataDocument,
  type IssueRow,
  IssuesDocument,
  type StageOption,
} from "#app/lib/data";
import { useIssueBulkActions } from "#app/components/issues/use-issue-bulk-actions";
import { useIssuesTableColumns } from "#app/components/issues/use-issues-table-columns";
import { useIssuesTableFilters } from "#app/components/issues/use-issues-table-filters";
import {
  FIELD_TO_DIMENSION,
  groupWhereForField,
  useIssuesTableGrouping,
} from "#app/components/issues/use-issues-table-grouping";
import { IssueMenuItemsWithActions } from "#app/components/issue-actions/issue-menu-items";
import { useItemShareDialogActions } from "#app/lib/item-share-dialog-context";
import { canManageSharing } from "#app/lib/utils/access-level";
import { useDeleteIssueWithUndo } from "#app/hooks/kanban/use-delete-issue-with-undo";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import {
  useAllMemberItems,
  useUserPool,
  useTeamPool,
} from "#app/components/entity-selectors";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { MigrateIssueTypeDialog } from "#app/components/issue-view/migrate-issue-type-dialog";
import {
  IssueRenameProvider,
  useIssueRenameControls,
} from "#app/components/issues/issue-rename-context";
import {
  ISSUE_SORT_DEFAULT_DIRECTION,
  ISSUE_SORT_ICONS,
  ISSUE_SORT_LABELS,
  AGE_GROUPS,
  UNASSIGNED_DIMENSION_LABEL,
} from "#app/components/issues/issue-grouping";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { issueListSubscriptions } from "#app/hooks/issue-list/issue-list-subscriptions";
import { useRegisterSubscriptions } from "#app/lib/subscriptions/subscription-provider";
import { useIssueView } from "#app/components/issue-view";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import { useResetOnChange } from "#app/lib/hooks/use-reset-on-change";
import { useWorkflowStages } from "#app/lib/hooks/use-workflow-stages";
import { useIssueTypes } from "#app/lib/hooks/use-issue-types";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAppData } from "#app/lib/data";
import {
  scopeWorkflowId,
  scopeKey,
  type IssueScope,
  scopeWorkflowIds,
} from "#app/lib/filters/issue-scope";

import { StageUpdateCycleMenu } from "#app/components/updates/stage-update-cycle-menu";
import {
  useAnalytics,
  AnalyticsContext,
  IssueEvent,
  AnalyticsView,
} from "#app/lib/analytics";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import {
  applyCurrentUserFilter,
  combineWhereConditions,
} from "#app/lib/query-utils";
import {
  GroupQueryProvider,
} from "#app/lib/hooks/group-query-provider";
import { at } from "#app/lib/utils/at";
import {
  ListDisplayFields,
  countShownListFields,
  ViewFieldsTrigger,
  ViewOptionsPanel,
  ViewOptionsTrigger,
  ViewSaveButton,
} from "#app/components/view-options";
import { VIEW_PANE_ANCHOR_ID } from "#app/lib/hooks/use-anchor-insets";
import {
  useViewConfigState,
  useViewsOptional,
} from "#app/lib/views/view-context";

// The bulk bar's count-chip noun: "1 Project selected".
const PROJECT_SELECTION_NOUN = { one: "Project", other: "Projects" };

/**
 * The one issue table, for any scope. A single workflow groups by stage and can
 * create projects + bulk-move stages; a team or member spans many workflows, so
 * it defaults to a flat list (stage is per-row editable, but no stage grouping,
 * no bulk-stage, no create). Everything workflow-specific is gated on
 * `scopeWorkflowId(scope)`. Self-contained — the route renders `<IssuesTable
 * scope=… />` and this owns its own data/filters/mutations via the shared
 * issue data layer (`GroupQueryProvider` + `useGroupStates`) and an ambient
 * `IssueFilterProvider` for panel filters.
 */

// Shared by the table (sticky pinning) and the column settings panel (locked
// rows). The panel ignores structural ids like "select".
const ISSUES_PINNED_COLUMN_IDS = ["select", "name"];

const getConnection = (data: any) => data?.issues;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IssuesTable({
  scope,
  onAddProject,
  onAddExistingProject,
  renderExtraRowActions,
  extraBulkActions,
}: Omittable<{
  scope: IssueScope;
  /**
   * Cross-workflow scopes have no single workflow to create into, so the
   * toolbar's "+ Add Project" is normally hidden for them. A host that owns
   * its own create flow (e.g. an account/contact detail tab prefilling the
   * new project) passes this to supply the button instead.
   */
  onAddProject?: () => void;
  /** Opens the host's link-existing-Projects picker. Detail-view tabs pass
   * this (alongside `onAddProject`), turning the toolbar's "+ Project" into a
   * split button whose menu offers create vs. link. Workflow views omit it —
   * a Project belongs to its workflow by construction, not by linking. */
  onAddExistingProject?: () => void;
  /** Host-specific row-menu items (e.g. an account/contact detail tab's
   * "Remove from Account"), rendered at the top of the row menu, ahead of
   * the shared issue actions. */
  renderExtraRowActions?: (issue: IssueRow) => ReactNode;
  /** Host-specific bulk-bar actions (e.g. an account/contact detail tab's
   * "Remove from Account" unlink), appended after the shared bulk actions. */
  extraBulkActions?: BulkAction<IssueRow>[];
}>) {
  const workflowId = scopeWorkflowId(scope);
  // List-level realtime is workflow-keyed; inert for cross-workflow scopes.
  // Shares wire subscriptions with a mounted board on the same workflow.
  useRegisterSubscriptions(
    workflowId ? issueListSubscriptions(workflowId) : [],
  );
  const { track } = useAnalytics();

  // --- Data wiring (self-contained, driven by scope) ---
  const { openIssue, openCreateIssue } = useIssueView();
  const { changeStage, updateAssignees, moveToWorkflow } = useIssueListMutations();
  const refetchGroups = useRefetchIssueGroups();

  const { pool: userPool } = useUserPool();
  const { pool: teamPool, baseItems: teamItems } = useTeamPool();
  // The full roster, not the windowed selector pool — the assignee group
  // bands must list EVERY member, not just the pool's first page. The pools
  // stay for the filter configs, where windowed + server search is right.
  const allMembers = useAllMemberItems();
  const { workflows } = useAppData();

  // Every display knob on this list — grouping, archived, empty groups, the
  // column set's key, Only me — is one `ViewConfig`. Inside a workflow's
  // saved-view scope it is the view's config and the panel's Save commits it;
  // on the team and member lists, which have no view row, it is session state.
  // A single workflow groups by stage. "Shared with me" does too — every
  // workflow it spans is a section over that workflow's stages, so a hand-off
  // reads in its own pipeline; the other cross-workflow lists stay flat.
  const isSharedScope = scope.kind === "shared";
  const listDefaults = useMemo(
    () => ({
      groupBy: workflowId || isSharedScope ? "stage" : "none",
    }),
    [workflowId, isSharedScope],
  );
  const { config, setConfig } = useViewConfigState("list", listDefaults);
  // A team's list widens to the Projects shared with the team on the view's
  // "Show shared Projects" switch; every other scope is what it is.
  const viewScope = useMemo<IssueScope>(
    () => (scope.kind === "team" ? { ...scope, includeShared: config.showShared } : scope),
    [scope, config.showShared],
  );
  const views = useViewsOptional();
  const activeViewId = views?.activeView?.id ?? null;
  const openFieldsPanel = views?.activeView ? views.openPanel : null;

  // The toolbar's Only Me is a SESSION overlay on the saved position: a
  // passing "just my work?" press is not an edit to a shared view, so it
  // never drafts. The panel's switch is the saved path — it writes the draft,
  // which previews here because any move of the config value (a panel edit,
  // a save, a discard, a view switch) clears the overlay.
  const [onlyMeSession, setOnlyMeSession] = useState<boolean | null>(null);
  useResetOnChange(`${activeViewId ?? ""}|${String(config.onlyMe)}`, () =>
    setOnlyMeSession(null),
  );
  const {
    isCurrentUserButtonSelected,
    isCurrentUserFilterApplied,
    isDesktop,
    handleCurrentUserPress,
    handleCurrentUserClear,
  } = useCurrentUserFilterToggle({
    selected: onlyMeSession ?? config.onlyMe,
    onSelectedChange: setOnlyMeSession,
  });

  // Collapsed groups ARE view config: a group-header toggle drafts through
  // the same channel as the panel's controls, so collapsing a section raises
  // Save. The table's known-empty-starts-closed default stays session-side —
  // only explicit collapses land in the set. Sorted so the same set always
  // dirty-compares identically.
  const collapsedGroupKeys = useMemo(
    () => new Set(config.collapsedGroups),
    [config.collapsedGroups],
  );
  const handleCollapsedGroupsChange = useCallback(
    (next: Set<string>) => setConfig({ collapsedGroups: [...next].sort() }),
    [setConfig],
  );

  // Stages: a single workflow uses that workflow's ordered stages; a
  // cross-workflow scope resolves labels from every workspace stage so the
  // Stage column never renders "Unknown" for a row in another workflow.
  const { stages: workflowStages } = useWorkflowStages(workflowId);
  const { stages: workspaceStages, stagesByWorkflow } = useWorkspaceData();
  const availableStages = useMemo<StageOption[]>(
    () => (workflowId ? workflowStages : workspaceStages),
    [workflowId, workflowStages, workspaceStages],
  );
  // The workflows a cross-workflow list can group by: the scope's own set
  // when it names one, otherwise every non-archived workflow in the sidebar
  // order. A group of a workflow the viewer reaches only through a shared
  // project still renders — its rows are what "Shared with me" is for.
  const scopedWorkflowIds = scopeWorkflowIds(scope);
  const scopedWorkflows = useMemo(
    () =>
      workflows.filter(
        (wf) =>
          (scopedWorkflowIds ? scopedWorkflowIds.includes(wf.workflowId) : !wf.archived),
      ),
    [workflows, scopedWorkflowIds],
  );
  // Stage groups for a single workflow are its own roster; across workflows
  // they are every scoped workflow's roster in turn, sidebar order then stage
  // order, so each workflow's stages sit together under its section band.
  const stageGroupOptions = useMemo(
    () =>
      workflowId
        ? availableStages.map((s) => ({ key: s.stageId, label: s.name }))
        : scopedWorkflows.flatMap((wf) =>
            (stagesByWorkflow.get(wf.workflowId) ?? []).map((s) => ({
              key: s.stageId,
              label: s.name,
            })),
          ),
    [workflowId, availableStages, scopedWorkflows, stagesByWorkflow],
  );

  const issueTypes = useIssueTypes();

  const handleOpenIssue = useCallback(
    (issue: IssueRow) => openIssue(issue, { source: "list_row" }),
    [openIssue],
  );


  // --- Inline rename ---
  // The row menu's Rename puts that row's name cell into edit mode; commit
  // goes through the same optimistic rename mutation. The state reaches the
  // name cell through IssueRenameProvider, never through the column defs —
  // see issue-rename-context.tsx.
  const renameControls = useIssueRenameControls();
  const { onRenameStart } = renameControls;

  // --- Columns ---
  const {
    allColumns,
    customFields,
    renderCustomFieldMenuItems,
    columnState,
    ageDialogIssue,
    handleAgeDialogClose,
    typeMigration,
    setTypeMigration,
    issueTypesWithFields,
    handleTypeMigrationConfirm,
  } = useIssuesTableColumns({
    workflowId,
    availableStages,
    handleOpenIssue,
    changeStage,
    updateAssignees,
    moveToWorkflow,
    activeViewId,
    viewsLoading: views?.initialLoading ?? false,
    viewColumns: config.columns,
    setConfig,
  });

  // Group buckets: assignee is users-only; teams are their own dimension.
  const userGroups = useMemo(() => {
    const groups = allMembers.map((item) => ({
      key: item.id,
      label: item.title,
    }));
    groups.sort((a, b) => a.label.localeCompare(b.label));
    return groups;
  }, [allMembers]);

  const teamGroups = useMemo(() => {
    const groups = teamItems.map((item) => ({
      key: item.id,
      label: item.title,
    }));
    groups.sort((a, b) => a.label.localeCompare(b.label));
    return groups;
  }, [teamItems]);

  // --- Data table state (selection, grouping) ---
  // Stage grouping needs a single workflow, so cross-workflow scopes omit it
  // from the picker and offer workflow instead — the board's cross-workflow
  // dimension, counted server-side by `workflowId`.
  const {
    columns,
    groupByField,
    setGroupByField,
    isGrouped,
    groupByLabels,
    groupByIcons,
    activeGroupConfig,
    groupSortDirection,
    setGroupSortDirection,
  } = useDataTableState({
    columns: allColumns,
    groupByField: config.groupBy,
    onGroupByFieldChange: (field) => setConfig({ groupBy: field }),
    groupSortDirection: config.groupDirection,
    onGroupSortDirectionChange: (direction) =>
      setConfig({ groupDirection: direction }),
    groupFields: {
      ...(workflowId || isSharedScope
        ? {
            stage: {
              label: "Stage",
              icon: PROPERTY_ICON.stage,
              // No unassigned bucket: an issue can never lack a stage (create
              // always resolves one, update rejects null/unknown stage ids),
              // and the board builds no unassigned stage column either.
              groups: stageGroupOptions,
              filterKey: "stageId",
              // Stages follow the settings order (type sections, then
              // sortOrder) — never alphabetized. Sort toggle = reversed.
              preserveOrder: true,
            },
          }
        : {}),
      assignees: {
        label: "Assignee",
        icon: ASSIGNEE_ICON,
        groups: [
          ...userGroups,
          {
            key: UNASSIGNED_GROUP_KEY,
            label: UNASSIGNED_DIMENSION_LABEL.assignee,
          },
        ],
        filterKey: "assignees",
      },
      // The project's own Teams property (the Assigned Team filter) — the
      // ONLY team dimension offered here. Grouping by the assignees' teams is
      // left out on purpose: it double-counts multi-team assignments and
      // shifts with roster changes.
      // That question is the Assignee filter's Teams half, and the shared
      // grouping plumbing (FIELD_TO_DIMENSION, header rendering) keeps
      // supporting it.
      teams: {
        label: "Teams",
        icon: PROPERTY_ICON.team,
        groups: [
          ...teamGroups,
          { key: UNASSIGNED_GROUP_KEY, label: UNASSIGNED_DIMENSION_LABEL.teams },
        ],
        filterKey: "teamIds",
      },
      age: {
        label: "Follow up",
        icon: PROPERTY_ICON.followUp,
        groups: [...AGE_GROUPS],
        preserveOrder: true,
      },
      ...(workflowId
        ? {}
        : {
            workflow: {
              label: "Workflow",
              icon: PROPERTY_ICON.workflow,
              // No unassigned bucket — a project always belongs to a workflow.
              // Workflows follow the workspace's sidebar order, never
              // alphabetized; the sort toggle reverses it.
              groups: scopedWorkflows.map((wf) => ({
                key: wf.workflowId,
                label: wf.name ?? "",
              })),
              filterKey: "workflowId",
              preserveOrder: true,
            },
          }),
      issueType: {
        label: "Project Type",
        icon: PROPERTY_ICON.issueType,
        // No unassigned bucket — every issue carries a type (NOT NULL,
        // creation falls back to the workspace default).
        groups: issueTypes.map((t) => ({ key: t.issueTypeId, label: t.name })),
        filterKey: "issueTypeId",
      },
    },
  });
  // The main page's Fields row readout — the Fields page's own Shown count,
  // the pinned Name column aside.
  const shownFieldCount = useMemo(
    () =>
      countShownListFields({
        columns,
        fields: customFields,
        pinnedColumnIds: ISSUES_PINNED_COLUMN_IDS,
        columnOrder: columnState.settingsProps.columnOrder,
        columnVisibility: columnState.settingsProps.columnVisibility,
      }),
    [
      columns,
      customFields,
      columnState.settingsProps.columnOrder,
      columnState.settingsProps.columnVisibility,
    ],
  );

  // The grouping dimensions this list offers, for the Customize view panel's
  // Group by row. A list CAN render flat, so it does not require a grouping.
  const groupByPanel = useMemo(
    () => ({ labels: groupByLabels, icons: groupByIcons }),
    [groupByLabels, groupByIcons],
  );

  // The sort fields this list offers, for the panel's Sort by row. Static:
  // the vocabulary is the server's orderBy keys, not the column roster.
  const sortByPanel = useMemo(
    () => ({
      labels: ISSUE_SORT_LABELS,
      icons: ISSUE_SORT_ICONS,
      defaultDirections: ISSUE_SORT_DEFAULT_DIRECTION,
    }),
    [],
  );

  // --- Filters ---
  const {
    tableFilters,
    panelFilters,
    screenFilterWhere,
    screenFilterCount,
    viewNoteCount,
    quickLayerFor,
    clearAllFilters,
    filterConfig,
    filterPanelOpen,
    setFilterPanelOpen,
    stageWhere,
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    searchWhere,
    orderBy,
    where,
    handOrder,
    groupOrderBy,
  } = useIssuesTableFilters({
    scope: viewScope,
    availableStages,
    userPool,
    teamPool,
    isCurrentUserFilterApplied,
    isGrouped,
    groupByField,
    config,
    setConfig,
    workspaceStages,
  });

  const deleteWithUndo = useDeleteIssueWithUndo();
  const { openDialog: openShareDialog } = useItemShareDialogActions();

  const renderRowActions = useCallback(
    (issue: IssueRow) => (
      <IssueMenuItemsWithActions
        issue={issue}
        prepend={renderExtraRowActions?.(issue)}
        onRename={() => onRenameStart(issue.issueId)}
        onDelete={() => { void deleteWithUndo(issue); }}
        onShare={() => openShareDialog(issue.issueId)}
        canManageSharing={canManageSharing(issue.viewerAccessLevel)}
        canDelete={!!issue.viewerCanDelete}
        canArchive={!!issue.viewerCanArchive}
        // The mutating rows (Rename, Stage, Assignee, Move, Type) ride the
        // same gate as the row's inline editors — a comment-level viewer
        // gets the read-only roster, not a menu door around the cells.
        readOnly={!issue.viewerCanEdit}
      />
    ),
    [
      onRenameStart,
      deleteWithUndo,
      openShareDialog,
      renderExtraRowActions,
    ],
  );

  // --- Grouping ---
  const {
    states,
    registerState,
    activatedKeys,
    handleGroupActivate,
    visibleGroups,
    groupBaseWhere,
    groupCountsRecord,
    isGroupCountsReady,
    getGroupWhere,
    isTrulyEmpty,
    isFilteredEmpty,
    isCurrentUserEmpty,
    groups,
    renderGroupTitle,
    renderWorkflowTitle,
    groupTitleInteractive,
    renderGroupActions,
    renderGroupFooter,
    rowDrag,
    cycleStage,
    cycleAnchor,
    setCycleStageId,
  } = useIssuesTableGrouping({
    scope: viewScope,
    workflowId,
    isSharedScope,
    isGrouped,
    activeGroupConfig,
    groupByField,
    groupSortDirection,
    tableFilters,
    screenFilterWhere,
    searchWhere,
    searchValue,
    panelFilterCount: panelFilters.activeFilterCount,
    showArchived: config.showArchived,
    stageWhere,
    isCurrentUserFilterApplied,
    where,
    availableStages,
    workflows,
    workspaceStages,
    memberItems: allMembers,
    teamItems,
    issueTypes,
    handOrder,
    openCreateIssue,
  });

  // --- Bulk actions ---
  const bulkActions = useIssueBulkActions({
    view: AnalyticsView.List,
    scope: scope.kind,
    workflowId,
    availableStages,
    refetchGroups,
    getGroupWhere,
    extraBulkActions,
  });

  // Create needs a single workflow to add into, unless the host supplies its
  // own create flow (a detail tab that prefills the link it is creating from).
  const handleAddProject = useMemo(
    () =>
      onAddProject ??
      (workflowId
        ? () => openCreateIssue({ workflowId, source: "list_view" })
        : undefined),
    [onAddProject, workflowId, openCreateIssue],
  );

  return (
    <IssueRenameProvider value={renameControls}>
      {/* Per-group query providers — only mounted for activated groups */}
      {isGrouped &&
        FIELD_TO_DIMENSION[groupByField] &&
        visibleGroups
          .filter((g) => activatedKeys.has(g.key))
          .map((g) => {
            const groupWhere = applyCurrentUserFilter(
              combineWhereConditions(
                [
                  groupBaseWhere,
                  groupWhereForField(groupByField, g.key),
                ].filter(Boolean) as Record<string, unknown>[],
              ),
              isCurrentUserFilterApplied ?? false,
            );
            return (
              <GroupQueryProvider<IssueRow>
                key={g.key}
                groupKey={g.key}
                document={IssuesDocument}
                variables={{ where: groupWhere, orderBy: groupOrderBy, limit: 50 }}
                getConnection={getConnection}
                onStateChange={registerState}
              />
            );
          })}

      {/* Ungrouped query provider — a single workflow co-fetches its stages via
          IssueListDataDocument; a cross-workflow scope reads plain Issues. */}
      {!isGrouped &&
        (workflowId ? (
          <GroupQueryProvider<IssueRow>
            groupKey="__all__"
            document={IssueListDataDocument}
            variables={{ where, orderBy, workflowId, limit: 50 }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ) : (
          <GroupQueryProvider<IssueRow>
            groupKey="__all__"
            document={IssuesDocument}
            variables={{ where, orderBy, limit: 50 }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ))}

      {/* The view's toolbar band, below the tab strip. Page-default
          background, no bottom hairline — it sits open over the list. */}
      <Box background="background">
        <DataTableToolbar>
          <DataTableToolbarLeft>
            <DataTableGroupByDropdown
              groupByField={groupByField}
              setGroupByField={(field) => {
                setGroupByField(field);
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ChangeGrouping,
                  data: {
                    view: AnalyticsView.List,
                    scope: scope.kind,
                    dimension: field,
                    via: "toolbar",
                  },
                });
              }}
              groupByLabels={groupByLabels}
              groupByIcons={groupByIcons}
              groupSortDirection={groupSortDirection}
              setGroupSortDirection={setGroupSortDirection}
            />
            {/* Inside a saved view the columns affordance is the panel's
                Fields page; this stateless trigger jumps straight there. */}
            <ViewFieldsTrigger />
          </DataTableToolbarLeft>
          <DataTableToolbarActions>
            {/* First in the cluster: unsaved edits are the thing to
                answer before anything else in the row. Absent while
                the view matches what is saved. */}
            <ViewSaveButton />
            {/* Filter cluster in the shared cross-view order: the quick-filter
                chip, then Only-Me, then search. */}
            {/* The panel renders the chip and hangs off it — a popover
                anchored to the control that opened it belongs beside that
                control, not up in the route. */}
            <FilterPanel
              open={filterPanelOpen}
              onOpenChange={setFilterPanelOpen}
              config={filterConfig}
              filters={panelFilters}
              scope={scope}
              viewFilters={
                views && viewNoteCount > 0
                  ? {
                      count: viewNoteCount,
                      onEdit: () => views.openPanel("filters", "filter_chip"),
                    }
                  : undefined
              }
              onClearAll={clearAllFilters}
              layerFor={quickLayerFor}
              trigger={
                // The chip counts BOTH layers — how narrowed the screen is —
                // and its × clears both; a view filter cleared here is a
                // view edit, so Save appears for it.
                <FilterToggle
                  size="xs"
                  icon="filter"
                  count={screenFilterCount}
                  onClear={() => clearAllFilters("chip")}
                  tooltip="Quickly filter your Projects"
                  clearTooltip="Clear all filters"
                />
              }
            />
            {/* The Assignee filter's own door — the same value the panel's
                Assignee row edits, one press from Unassigned. */}
            <AssigneeFilterChip
              config={filterConfig}
              filters={quickLayerFor(ASSIGNEE_PRINCIPAL_FIELD)}
              scope={scope}
              size="xs"
              tooltip="Filter Projects by assignee"
            />
            <CurrentUserButton
              selected={isCurrentUserButtonSelected}
              onClick={() => {
                const enabled = isDesktop ? true : !isCurrentUserButtonSelected;
                handleCurrentUserPress();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.List, scope: scope.kind, enabled },
                });
              }}
              onClearClick={() => {
                handleCurrentUserClear();
                track({
                  context: AnalyticsContext.Issue,
                  event: IssueEvent.ToggleOnlyMe,
                  data: { view: AnalyticsView.List, scope: scope.kind, enabled: false },
                });
              }}
              size="xs"
            />
            <ViewSearch
              value={searchValue}
              onValueChange={setSearchValue}
              size="xs"
            />
            {/* The trailing group past the separator: the view's own controls
                (column settings beside the create action), set apart from the
                filter cluster. */}
            <Box direction="row" align="center" gap="sm">
              <Separator orientation="vertical" className="h-4" />
              {/* Outside a saved view the trigger renders nothing; columns
                  are then the table's own circle-plus picker. */}
              <ViewOptionsTrigger />
              {handleAddProject && (
                <ToolbarAction
                  // Text-only at full width ("Add Project", no plus); the
                  // glyph only appears when the action collapses to a bare
                  // "+" at compact widths, where the label would push the
                  // toolbar row past the screen's edge.
                  collapsedIcon="plus"
                  label="Add Project"
                  aria-label="Add Project"
                  tooltip="Add a Project to this location"
                  onClick={handleAddProject}
                  menuLabel="More Project actions"
                  // A host that can also LINK an existing Project splits the
                  // control: press creates, the chevron offers both. A
                  // workflow view omits it — a Project belongs to its
                  // workflow by construction, so there is nothing to link.
                  menuItems={
                    onAddExistingProject ? (
                      <>
                        <MenuItem onClick={handleAddProject}>
                          <Icon name="plus" size="md" color="muted" />
                          <Text size="sm">Create Project...</Text>
                        </MenuItem>
                        <MenuItem onClick={onAddExistingProject}>
                          <Icon name="link" size="md" color="muted" />
                          <Text size="sm">Add existing Projects...</Text>
                        </MenuItem>
                      </>
                    ) : undefined
                  }
                />
              )}
            </Box>
          </DataTableToolbarActions>
        </DataTableToolbar>
      </Box>
      {/* The view's panel belongs to the body, not the route: only the body
          knows which grouping dimensions and which Fields editor it offers.
          Outside a saved-view scope it renders nothing. */}
      <ViewOptionsPanel
        anchorId={VIEW_PANE_ANCHOR_ID}
        groupBy={groupByPanel}
        sortBy={sortByPanel}
        fields={{
          editor: (
            <ListDisplayFields
              columns={columns}
              fields={customFields}
              pinnedColumnIds={ISSUES_PINNED_COLUMN_IDS}
              {...columnState.settingsProps}
            />
          ),
          shownCount: shownFieldCount,
        }}
      />

      <Box direction="column" fill="flex">
        {isTrulyEmpty ? (
          <Box fill="flex" align="center" justify="center">
            <DataTableEmpty
              title="No Projects yet"
              icon="list"
              action={
                handleAddProject ? (
                  <Button size="sm" onClick={handleAddProject}>
                    <Text size="sm">Add Project</Text>
                  </Button>
                ) : undefined
              }
            />
          </Box>
        ) : isFilteredEmpty ? (
          <Box fill="flex" align="center" justify="center">
            <DataTableEmpty
              title="No Projects match these filters"
              icon="filter"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => clearAllFilters()}
                >
                  <Text size="sm">Clear filters</Text>
                </Button>
              }
            />
          </Box>
        ) : isCurrentUserEmpty ? (
          <Box fill="flex" align="center" justify="center">
            <DataTableEmpty
              title="No Projects assigned to you"
              icon="user"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCurrentUserClear}
                >
                  <Text size="sm">Turn off only me</Text>
                </Button>
              }
            />
          </Box>
        ) : (
          <DataTable
            key={scopeKey(scope)}
            columns={columns}
            groups={groups}
            getRowId={(r) => r.issueId}
            sorting={sorting}
            onSortingChange={setSorting}
            stickyHeader
            density="regular"
            // A team's list groups across workflows, so each group reads as
            // its own list and takes the wider gap; a workflow's stage groups
            // are one pipeline and keep the tight one.
            groupGap={scope.kind === "team" ? "lg" : "md"}
            // Breathing room under the toolbar band; scroll content, so rows
            // pass through it once the list scrolls.
            gutterTop="md"
            loading={
              // Grouped with the counts query in flight: `groups` is empty,
              // and the flag makes DataTable stand in with its delayed
              // skeleton-group mirror instead of a blank body.
              (isGrouped && !isGroupCountsReady) ||
              (groups.length === 1 &&
                at(groups, 0, "group").data.length === 0 &&
                (states.get(at(groups, 0, "group").key)?.loading ?? false))
            }
            pinnedColumnIds={ISSUES_PINNED_COLUMN_IDS}
            // Circle-plus on the actions column (the reference add-column
            // affordance). Inside a saved view it opens the Customize view
            // panel on its Fields page — the same editor the toolbar trigger
            // reaches. Outside one there is NO column control: this table
            // offers custom-field columns, and those are only ever picked in
            // the drawer — never the built-in picker popover, never
            // `DataTableColumnSettings`. `false` keeps the built-in picker
            // from mounting.
            addColumnHeader={
              openFieldsPanel ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Explore fields and columns"
                        onClick={() => openFieldsPanel("fields")}
                      />
                    }
                  >
                    <Icon name="circle-plus" color="faint" />
                  </TooltipTrigger>
                  <TooltipPopup side="top">
                    Explore fields and columns
                  </TooltipPopup>
                </Tooltip>
              ) : false
            }
            {...columnState.tableProps}
            // The header context menu's "Hide column" writes through the same
            // handler the Customize view panel uses. Only inside a saved view:
            // outside one there is no drawer to bring a hidden column back.
            {...(openFieldsPanel
              ? { onColumnVisibilityChange: columnState.settingsProps.onColumnVisibilityChange }
              : {})}
            // Custom-field columns add "Edit field" / "Automate field" to
            // their header context menus.
            renderColumnMenuItems={renderCustomFieldMenuItems}
            paddingX="md"
            lazyGroups={isGrouped}
            onGroupActivate={handleGroupActivate}
            // Stages are a canonical roster: an empty stage with its add-row
            // visible is pipeline information, so it starts open unless the
            // view's "Collapse empty stages" — on by default on a list — asks
            // for it closed. Open-ended
            // groupings (assignees, teams, age, type) keep the table's
            // known-empty-starts-collapsed heuristic.
            emptyGroupsStartOpen={
              groupByField === "stage" && !config.collapseEmptyStages
            }
            collapsedGroupKeys={collapsedGroupKeys}
            onCollapsedGroupKeysChange={handleCollapsedGroupsChange}
            groupCounts={isGrouped ? groupCountsRecord : undefined}
            groupTitle={isGrouped ? renderGroupTitle : undefined}
            sectionTitle={renderWorkflowTitle}
            groupTitleInteractive={isGrouped ? groupTitleInteractive : undefined}
            groupFooter={renderGroupFooter}
            groupActions={renderGroupActions}
            bulkActions={bulkActions}
            bulkSelectionNoun={PROJECT_SELECTION_NOUN}
            canSelectRow={(issue) => issue.viewerCanEdit}
            rowDrag={rowDrag}
            // On touch a row is a tap to open and nothing else: the project
            // view carries every action the row's hover chrome would offer.
            pressOnlyOnTouch
            // The whole row opens the project (reference parity): the guarded
            // row click covers the dead zones — the packed layout's trailing
            // filler, cell padding — while every interactive cell still
            // claims its own press.
            onRowClick={(row) => handleOpenIssue(row.original)}
            renderRowActions={renderRowActions}
            emptyState={
              <DataTableEmpty
                message={
                  searchValue
                    ? "No Projects match your search"
                    : "No Projects found"
                }
              />
            }
          />
        )}
      </Box>

      <StageUpdateCycleMenu
        open={cycleStage != null}
        onOpenChange={(open) => {
          if (!open) setCycleStageId(null);
        }}
        stage={cycleStage}
        anchor={cycleAnchor}
      />

      {ageDialogIssue && (
        <UpdateAgeDialog
          open
          onOpenChange={handleAgeDialogClose}
          issueId={ageDialogIssue.issueId}
          age={ageDialogIssue.age}
          stageId={ageDialogIssue.stageId}
          via="list_row"
        />
      )}

      {typeMigration && (
        <MigrateIssueTypeDialog
          open
          onOpenChange={(o) => {
            if (!o) setTypeMigration(null);
          }}
          issueTypes={issueTypesWithFields}
          issueTypeId={typeMigration.issueTypeId}
          values={typeMigration.issue.values ?? {}}
          issueName={typeMigration.issue.name}
          onConfirm={handleTypeMigrationConfirm}
        />
      )}
    </IssueRenameProvider>
  );
}
